import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { AgentClientService, PoliticsStatus } from '../client/agent-client';
import { StorageService } from '../shared/storage/storage.service';
import { SecurityService } from '../shared/security';

/** Chave do modo escolhido; sobrevive ao fechamento do app de propósito. */
const POLITICS_MODE_KEY = 'rsm.politics.mode';

/**
 * Modo de operação do app: comercial (padrão) ou rede de apoio voluntário.
 *
 * O modo é só uma declaração da interface — quem garante o isolamento dos dados
 * é o servidor, que recebe o escopo e filtra. Se este serviço errar, o pior que
 * acontece é a tela ficar com o rótulo errado; missão comercial não vaza para
 * dentro do ambiente Politics por causa disso.
 */
@Injectable({ providedIn: 'root' })
export class PoliticsModeService {

  private readonly modeSubject = new BehaviorSubject<'COMMERCIAL' | 'POLITICS'>('COMMERCIAL');
  private readonly statusSubject = new BehaviorSubject<PoliticsStatus | null>(null);

  /** Modo atual, consumido pela navegação, pelo vocabulário e pelos guards. */
  public readonly mode$ = this.modeSubject.asObservable();

  /** Situação do usuário no programa (vínculo, aceite, quarentena). */
  public readonly status$ = this.statusSubject.asObservable();

  constructor(
    private agentClientService: AgentClientService,
    private storageService: StorageService,
    private securityService: SecurityService,
  ) { }

  public get isPolitics(): boolean {
    return this.modeSubject.value === 'POLITICS';
  }

  public get status(): PoliticsStatus | null {
    return this.statusSubject.value;
  }

  /** O modo só pode ser ligado por quem tem vínculo E aceitou o termo vigente. */
  public get canUsePolitics(): boolean {
    return this.statusSubject.value?.active === true;
  }

  /**
   * Já apoiador, mas o termo mudou: precisa reconfirmar antes de voltar ao
   * ambiente. Enquanto isso a pessoa segue no comercial — nunca sem acesso.
   */
  public get needsTermsReacceptance(): boolean {
    return this.statusSubject.value?.termsUpdatePending === true;
  }

  /**
   * Carrega a situação do usuário e restaura o modo escolhido da última vez.
   *
   * <p>A restauração é a razão de o modo ser persistido: quem está atuando como
   * apoiador não deve ter que reescolher o ambiente a cada abertura do app.</p>
   *
   * <p>Falha na consulta NÃO derruba nada: o app segue no comercial, que é o
   * comportamento de sempre.</p>
   */
  public refresh(): Observable<PoliticsStatus | null> {
    const userId = this.securityService.credential?.userId;
    if (!userId) {
      this.reset();
      return of(null);
    }

    return this.agentClientService.getPoliticsStatus(userId).pipe(
      tap((status) => {
        this.statusSubject.next(status);
        this.restoreMode(status);
      }),
      catchError(() => {
        this.reset();
        return of(null);
      }),
    );
  }

  /**
   * Alterna entre os ambientes. Ligar o Politics exige adesão ativa; desligar é
   * sempre permitido, para o apoiador nunca ficar preso no ambiente.
   */
  public toggle(): void {
    this.setMode(this.isPolitics ? 'COMMERCIAL' : 'POLITICS');
  }

  public setMode(mode: 'COMMERCIAL' | 'POLITICS'): void {
    if (mode === 'POLITICS' && !this.canUsePolitics) {
      return;
    }
    this.modeSubject.next(mode);
    this.storageService.set(POLITICS_MODE_KEY, mode);
  }

  /** Volta ao comercial e esquece a escolha — usado no logout e na saída da rede. */
  public reset(): void {
    this.statusSubject.next(null);
    this.modeSubject.next('COMMERCIAL');
    this.storageService.remove(POLITICS_MODE_KEY);
  }

  private restoreMode(status: PoliticsStatus | null): void {
    if (status?.active !== true) {
      // Perdeu a adesão (saiu da rede, termo novo pendente): cai no comercial e
      // a escolha antiga é descartada, para não ressuscitar na próxima abertura.
      this.modeSubject.next('COMMERCIAL');
      this.storageService.remove(POLITICS_MODE_KEY);
      return;
    }

    // StorageService.get devolve `this.storage?.get(...)`: enquanto o Ionic
    // Storage não terminou o init, o retorno é undefined e encadear .then() aqui
    // estoura TypeError. Promise.resolve normaliza os dois casos.
    Promise.resolve(this.storageService.get(POLITICS_MODE_KEY))
      .then((saved) => {
        this.modeSubject.next(saved === 'POLITICS' ? 'POLITICS' : 'COMMERCIAL');
      })
      .catch(() => this.modeSubject.next('COMMERCIAL'));
  }
}
